import { useEffect, useState } from "react";

import { getOrganizationDocumentations } from "../../setup/api/services/organization";
import { IOrganizationDocumentation } from "../../setup/api/services/organization/models";

export enum EDocumentsState {
  LOADING = "LOADING",
  /** Documents are on file — show them back to the user instead of the upload step. */
  PRESENT = "PRESENT",
  /**
   * Nothing on file, OR the lookup failed and we can't tell. Both resolve to "let them upload":
   * a failed lookup must never be able to hide the upload step, which is the whole feature.
   */
  ABSENT = "ABSENT",
}

/**
 * Reports whether an organization already has attachments, so a returning user sees the review
 * screen with their documents rather than being asked to upload again.
 */
export function useOrganizationDocuments(code?: string, enabled = true) {
  const [result, setResult] = useState<{
    documents: IOrganizationDocumentation[];
    /** Which organization the result describes; anything else is stale. */
    resolvedFor?: string;
  }>({ documents: [] });

  useEffect(() => {
    if (!enabled || !code) return;

    let cancelled = false;
    getOrganizationDocumentations(code)
      .then((response) => {
        if (!cancelled) {
          setResult({ documents: response?.data ?? [], resolvedFor: code });
        }
      })
      .catch((error) => {
        console.warn(
          `[org-gate] Could not read attachments for "${code}"; treating as none on file.`,
          error
        );
        if (!cancelled) setResult({ documents: [], resolvedFor: code });
      });

    return () => {
      cancelled = true;
    };
  }, [code, enabled]);

  const settled = !!code && result.resolvedFor === code;
  // With no code there is nothing to look up and the effect above never runs, so this must not
  // report LOADING — that would hang the caller on a spinner forever. Falls back to ABSENT like
  // any other case we can't determine, which lets the user upload.
  const state =
    !enabled || !code
      ? EDocumentsState.ABSENT
      : // LOADING until the lookup settles, including the render between the code becoming
      // known and the effect firing — otherwise a user who has documents flashes the
      // upload step.
      !settled
      ? EDocumentsState.LOADING
      : result.documents.length
      ? EDocumentsState.PRESENT
      : EDocumentsState.ABSENT;

  return { state, documents: settled ? result.documents : [] };
}
